import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Table, Col, Row, Spinner } from "reactstrap"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import PageLoader from "./../../assets/loading/pageLoad1.gif"
import loader from "./../../assets/loading/loader.gif"
import { usePermissions } from "API/PermissionContext"

const CCIncentives = () => {
  const { canAdd, canDelete, MODULE } = usePermissions()
  const MID = MODULE.CC_INCENTIVES
  const [pageLoad, setPageLoad] = useState(true)
  const [tab, setTab] = useState(1)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  // Master data
  const [companies, setCompanies] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [allCCs, setAllCCs] = useState([])
  const [allRoutes, setAllRoutes] = useState([])
  const [vendors, setVendors] = useState([])
  const [charts, setCharts] = useState([])

  // Cascading
  const [cPlants, setCPlants] = useState([])
  const [cCCs, setCCCs] = useState([])
  const [cRoutes, setCRoutes] = useState([])
  const [cCollCenters, setCCollCenters] = useState([])

  // Form
  const [fApplyType, setFApplyType] = useState("1")
  const [fCompany, setFCompany] = useState("")
  const [fPlant, setFPlant] = useState("")
  const [fCC, setFCC] = useState("")
  const [fRoute, setFRoute] = useState("")
  const [fVendor, setFVendor] = useState("")
  const [fCollCenter, setFCollCenter] = useState("")
  const [fMilkType, setFMilkType] = useState("3")
  const [fSession, setFSession] = useState("3")
  const [fChart, setFChart] = useState("")
  const [fFrom, setFFrom] = useState("")
  const [fLimitValidity, setFLimitValidity] = useState("1")
  const [fValidUpto, setFValidUpto] = useState("")

  // Preview
  const [previewData, setPreviewData] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewChecked, setPreviewChecked] = useState({})
  const [previewAllChecked, setPreviewAllChecked] = useState(false)
  const [applyingSelected, setApplyingSelected] = useState(false)

  // Tab data
  const [tabData, setTabData] = useState([])
  const [tabLoading, setTabLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [page, setPage] = useState(1)
  const perPage = 50

  // Alerts
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [confirmType, setConfirmType] = useState("delete")
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")

  const a = r => { const d = r.data?.data; return Array.isArray(d) ? d : d?.data || [] }
  const showCompany = companies.length > 1

  const fetchMaster = useCallback(async () => {
    try {
      const [co, pl, cc, rt, ch, vn] = await Promise.all([
        api.get("companies"), api.get("plants"), api.get("centers"),
        api.get("routes"), api.get("active-incentive-charts"), api.get("vendors")
      ])
      setCompanies(a(co)); setAllPlants(a(pl)); setAllCCs(a(cc))
      setAllRoutes(a(rt)); setCharts(Array.isArray(ch.data?.data) ? ch.data.data : [])
      setVendors(Array.isArray(vn.data?.data) ? vn.data.data : vn.data?.data?.data || [])
    } catch (e) { console.error(e) }
    setPageLoad(false)
  }, [])

  useEffect(() => { fetchMaster() }, [fetchMaster])

  // Cascading handlers
  const onCompanyChange = async v => {
    setFCompany(v); setFPlant(""); setFCC(""); setFRoute(""); setFVendor(""); setFCollCenter("")
    setCPlants([]); setCCCs([]); setCRoutes([]); setCCollCenters([]); setPreviewData([])
    if (v) { try { setCPlants(a(await api.get("plants-by-company/" + v))) } catch (e) { } }
  }
  const onPlantChange = async v => {
    setFPlant(v); setFCC(""); setFRoute(""); setFVendor(""); setFCollCenter("")
    setCCCs([]); setCRoutes([]); setCCollCenters([]); setPreviewData([])
    if (v) { try { setCCCs(a(await api.get("chillings-by-plant/" + v))) } catch (e) { } }
  }
  const onCCChange = async v => {
    setFCC(v); setFRoute(""); setFVendor(""); setFCollCenter("")
    setCRoutes([]); setCCollCenters([]); setPreviewData([])
    if (v) { try { setCRoutes(a(await api.get("route-cc/" + v))) } catch (e) { } }
  }
  const onRouteChange = async v => {
    setFRoute(v); setFCollCenter(""); setPreviewData([])
    setCCollCenters([])
    if (v) { try { const r = await api.get("collection-center-route/" + v); setCCollCenters(Array.isArray(r.data?.data) ? r.data.data : r.data?.data?.data || []) } catch (e) { } }
  }

  // Load tab data
  const loadTabData = useCallback(async (t) => {
    setTabLoading(true); setCheckedItems({}); setParentChecked(false); setSearch(""); setPage(1)
    try {
      const endpoints = { 2: "cc-center-vendor-approvals", 3: "cc-incentive-approvals", 4: "cc-center-vendor-updates", 5: "cc-incentive-updates" }
      if (endpoints[t]) { const r = await api.get(endpoints[t]); setTabData(Array.isArray(r.data?.data) ? r.data.data : []) }
    } catch (e) { setTabData([]) }
    setTabLoading(false)
  }, [])

  useEffect(() => { if (tab >= 2) loadTabData(tab) }, [tab, loadTabData])

  // Preview: Show button handler
  const handleShow = async () => {
    if (!fChart) return setResult({ ok: false, msg: "Incentive Chart is required." })
    if (!fFrom) return setResult({ ok: false, msg: "Effective From Date is required." })
    if (fLimitValidity === "1" && !fValidUpto) return setResult({ ok: false, msg: "Valid Upto is required." })
    if (fApplyType === "3" && !fVendor && !fCC) return setResult({ ok: false, msg: "Select at least a Chilling Center or Vendor." })
    setPreviewLoading(true); setResult(null); setPreviewData([]); setPreviewChecked({}); setPreviewAllChecked(false)
    try {
      const resp = await api.post("cc-incentive-preview", {
        applyType: fApplyType, company: fCompany || null, plant: fPlant || null,
        chillingCenter: fCC || null, route: fRoute || null,
        collectionCenter: fCollCenter || null, vendor: fVendor || null,
        milkType: fMilkType, incentiveChart: fChart,
      })
      const d = resp.data?.data || []
      if (d.length === 0) setResult({ ok: false, msg: "No matching records found for the selected criteria." })
      setPreviewData(d)
    } catch (e) { setResult({ ok: false, msg: e.response?.data?.message || e.message }) }
    setPreviewLoading(false)
  }

  // Apply selected from preview
  const handleApplySelected = async () => {
    const selectedIds = Object.keys(previewChecked).filter(k => previewChecked[k])
    if (selectedIds.length === 0) return alert("Select at least one record to apply.")
    setApplyingSelected(true); setResult(null)
    try {
      const resp = await api.post("bulk-apply-cc-incentives", {
        applyType: fApplyType, entityIds: selectedIds, milkType: fMilkType,
        session: fSession, incentiveChart: fChart, effectiveFrom: fFrom,
        limitValidity: fLimitValidity, validUpto: fValidUpto || null,
        company: fCompany || null, plant: fPlant || null,
        chillingCenter: fCC || null, route: fRoute || null,
      })
      setResult({ ok: true, msg: resp.data?.message || "Incentives applied successfully!" })
      setPreviewData([]); setPreviewChecked({}); setPreviewAllChecked(false)
      setTimeout(() => setResult(null), 4000)
    } catch (e) { setResult({ ok: false, msg: e.response?.data?.message || e.message }) }
    setApplyingSelected(false)
  }

  // Preview checkbox helpers
  const togglePreviewAll = () => {
    const v = !previewAllChecked; setPreviewAllChecked(v)
    const m = {}; previewData.forEach(r => m[r.entityId] = v); setPreviewChecked(m)
  }
  const togglePreviewOne = id => {
    setPreviewChecked(p => {
      const n = { ...p, [id]: !p[id] }
      setPreviewAllChecked(previewData.every(r => n[r.entityId]))
      return n
    })
  }
  const previewSelectedCount = Object.keys(previewChecked).filter(k => previewChecked[k]).length

  // Helpers
  const getName = (list, id, key = "name") => { const r = list.find(x => String(x.id) === String(id)); return r ? (r[key] || r.name || r.chartName || id) : id || "All" }
  const milkLbl = v => ({ "1": "Cow", "2": "Buffalo", "3": "Both" }[String(v)] || v)
  const sessLbl = v => ({ "1": "AM", "2": "PM", "3": "Both" }[String(v)] || v)

  // Bulk actions for tabs
  const selectedIds = Object.keys(checkedItems).filter(k => checkedItems[k])
  const selectedCount = selectedIds.length
  const filtered = useMemo(() => {
    if (!search) return tabData
    const s = search.toLowerCase()
    return tabData.filter(r => JSON.stringify(r).toLowerCase().includes(s))
  }, [tabData, search])
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const paged = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page])
  useEffect(() => setPage(1), [search])
  const handleCheckbox = id => setCheckedItems(p => { const n = { ...p, [id]: !p[id] }; setParentChecked(filtered.every(r => n[r.id])); return n })
  const handleParentCheckbox = () => { const v = !parentChecked; setParentChecked(v); const m = {}; filtered.forEach(r => m[r.id] = v); setCheckedItems(m) }

  const handleBulkAction = async (type) => {
    try {
      if (type === "delete") await api.post("bulk-delete-cc-incentives", { ids: selectedIds })
      else await api.post("bulk-approve-cc-incentives", { ids: selectedIds })
      loadTabData(tab)
      setdynamic_title(type === "delete" ? "Deleted" : "Approved")
      setdynamic_description(`${selectedCount} item(s) ${type === "delete" ? "deleted" : "approved"}.`)
      setsuccess_dlg(true)
    } catch (e) { alert(e.message) }
  }
  const handleSingleDelete = async (id) => {
    try { await api.delete("delete-cc-incentive/" + id); loadTabData(tab) } catch (e) { alert(e.message) }
  }
  const handleSingleApprove = async (id) => {
    try { await api.put("approve-cc-incentive/" + id); loadTabData(tab) } catch (e) { alert(e.message) }
  }

  const ib = (bg, icon, title, onClick, dis) => <button title={title} onClick={onClick} disabled={dis} style={{ width: 32, height: 32, borderRadius: 4, background: bg, color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: "pointer", opacity: dis ? 0.5 : 1 }}><i className={`fas ${icon}`} /></button>

  const tabNames = ["Apply CC Incentives", "Center/Vendor Incentive Approvals", "CC Incentive Approvals", "Center/Vendor Incentive Updates", "CC Incentive Updates"]
  const isApprovalTab = tab === 2 || tab === 3
  const isCenterVendor = tab === 2 || tab === 4

  // Tab columns
  const baseCols = isCenterVendor
    ? ["#", "Company", "Plant", "Chilling Center", "Route", "Center/Vendor", "Incentive Chart", "Milk Type", "Session", "Effective From", "Validity", "Updated In App", "Applied On"]
    : ["#", "Company", "Plant", "Chilling Center", "Incentive Chart", "Milk Type", "Session", "Effective From", "Validity", "Updated In App", "Applied On"]
  const cols = isApprovalTab ? [...baseCols, "Action"] : baseCols

  return (<>
    {pageLoad ? <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div> : <>
      <div className="incentivePanel">
        {tabNames.map((t, i) => <span key={i} className={tab === i + 1 ? "acActive" : ""} style={{ cursor: "pointer" }} onClick={() => setTab(i + 1)}>{t}</span>)}
      </div>

      {tab === 1 ? (
        <div className="dashBody" style={{ marginTop: 10 }}>
          <div className="font-size-18 mb-0 dashHeader"><Row><Col md={6}><h4>Apply CC Incentive Charts</h4></Col></Row></div>
          <div className="dashContent" style={{ padding: "16px 20px" }}>
            {result && <div className={`alert ${result.ok ? "alert-success" : "alert-danger"} py-2`} style={{ fontSize: 13 }}>{result.msg}</div>}

            {/* Row 1: Apply Type + cascading */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 14 }}>
              <div style={{ flex: "1 1 250px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Apply Incentive/Commission <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ display: "flex", gap: 12, paddingTop: 4, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13 }}><input type="radio" name="applyType" value="1" checked={fApplyType === "1"} onChange={e => { setFApplyType(e.target.value); setPreviewData([]) }} style={{ marginRight: 4 }} />Common for all Centers/Vendors</label>
                  <label style={{ fontSize: 13 }}><input type="radio" name="applyType" value="2" checked={fApplyType === "2"} onChange={e => { setFApplyType(e.target.value); setPreviewData([]) }} style={{ marginRight: 4 }} />Agents/Center wise</label>
                  <label style={{ fontSize: 13 }}><input type="radio" name="applyType" value="3" checked={fApplyType === "3"} onChange={e => { setFApplyType(e.target.value); setPreviewData([]) }} style={{ marginRight: 4 }} />Vendor wise</label>
                </div>
              </div>
              {showCompany && <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Company</label>
                <select className="form-select form-select-sm" value={fCompany} onChange={e => onCompanyChange(e.target.value)}>
                  <option value="">All</option>
                  {companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
                </select>
              </div>}
              <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Plant</label>
                <select className="form-select form-select-sm" value={fPlant} onChange={e => onPlantChange(e.target.value)}>
                  <option value="">All</option>
                  {(fCompany ? cPlants : allPlants).map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Chilling Center</label>
                <select className="form-select form-select-sm" value={fCC} onChange={e => onCCChange(e.target.value)}>
                  <option value="">All</option>
                  {(fPlant ? cCCs : allCCs).map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {fApplyType === "2" && <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Route</label>
                <select className="form-select form-select-sm" value={fRoute} onChange={e => onRouteChange(e.target.value)}>
                  <option value="">All</option>
                  {(fCC ? cRoutes : allRoutes).map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}
                </select>
              </div>}
            </div>

            {/* Row 2: Vendor/Center + Milk + Session */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 14 }}>
              {fApplyType === "2" && <>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Collection Center</label>
                  <select className="form-select form-select-sm" value={fCollCenter} onChange={e => setFCollCenter(e.target.value)}>
                    <option value="">All</option>
                    {(fRoute ? cCollCenters : []).map((c, i) => <option key={i} value={c.id}>{c.centerName || c.name}</option>)}
                  </select>
                </div>
              </>}
              {fApplyType === "3" && <>
                <div style={{ flex: "1 1 200px" }}>
                  <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Vendor <span style={{ color: "#dc3545" }}>*</span></label>
                  <select className="form-select form-select-sm" value={fVendor} onChange={e => setFVendor(e.target.value)}>
                    <option value="">All Vendors</option>
                    {vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName || v.name}</option>)}
                  </select>
                </div>
              </>}
              <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Milk Type <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
                  {[["1", "Cow"], ["2", "Buffalo"], ["3", "Both"]].map(([v, l]) => <label key={v} style={{ fontSize: 13 }}><input type="radio" name="milkType" value={v} checked={fMilkType === v} onChange={e => setFMilkType(e.target.value)} style={{ marginRight: 3 }} />{l}</label>)}
                </div>
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Applicable Session</label>
                <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
                  {[["3", "Both"], ["1", "AM"], ["2", "PM"]].map(([v, l]) => <label key={v} style={{ fontSize: 13 }}><input type="radio" name="session" value={v} checked={fSession === v} onChange={e => setFSession(e.target.value)} style={{ marginRight: 3 }} />{l}</label>)}
                </div>
              </div>
            </div>

            {/* Row 3: Chart + Date + Validity */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 14 }}>
              <div style={{ flex: "1 1 220px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Incentive Chart To Apply <span style={{ color: "#dc3545" }}>*</span></label>
                <select className="form-select form-select-sm" value={fChart} onChange={e => setFChart(e.target.value)}>
                  <option value="">Select</option>
                  {charts.map((c, i) => <option key={i} value={c.id}>{c.chartName}</option>)}
                </select>
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Effective From Date <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="date" className="form-control form-control-sm" value={fFrom} onChange={e => setFFrom(e.target.value)} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Limit Validity <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
                  <label style={{ fontSize: 13 }}><input type="radio" name="validity" value="1" checked={fLimitValidity === "1"} onChange={e => setFLimitValidity(e.target.value)} style={{ marginRight: 3 }} />Yes</label>
                  <label style={{ fontSize: 13 }}><input type="radio" name="validity" value="0" checked={fLimitValidity === "0"} onChange={e => setFLimitValidity(e.target.value)} style={{ marginRight: 3 }} />No</label>
                </div>
              </div>
              {fLimitValidity === "1" && <div style={{ flex: "1 1 180px" }}>
                <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: 13 }}>Valid Upto <span style={{ color: "#dc3545" }}>*</span></label>
                <input type="date" className="form-control form-control-sm" value={fValidUpto} onChange={e => setFValidUpto(e.target.value)} />
              </div>}
            </div>

            <button className="btn btn-primary btn-sm" onClick={handleShow} disabled={previewLoading}>
              {previewLoading ? <><Spinner size="sm" /> Loading...</> : "Show"}
            </button>


            {/* Preview Table */}
            {previewData.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <h6 style={{ margin: 0, fontWeight: 600, color: "#495057" }}>Preview: {previewData.length} record(s) found</h6>
                  <button className="btn btn-success btn-sm" onClick={handleApplySelected} disabled={applyingSelected || previewSelectedCount === 0}>
                    {applyingSelected ? <><Spinner size="sm" /> Applying...</> : `Apply Selected Incentive Charts (${previewSelectedCount})`}
                  </button>
                </div>
                <div className="table-responsive">
                  <Table className="table table-sm table-bordered table-hover mb-0 dairyTbl" style={{ fontSize: 13 }}>
                    <thead style={{ background: "#f8f9fa" }}>
                      <tr>
                        <th style={{ width: 28 }}><input type="checkbox" checked={previewAllChecked} onChange={togglePreviewAll} /></th>
                        {fApplyType === "1" && <><th>CC Number</th><th>CC Name</th></>}
                        {fApplyType === "2" && <><th>Chilling Center</th><th>Route</th><th>Center Number</th><th>Center Name</th></>}
                        {fApplyType === "3" && <><th>Chilling Center</th><th>Route</th><th>Vendor Number</th><th>Vendor Name</th></>}
                        <th>Milk Type</th><th>Incentive Setting</th><th>Existing Inc Chart</th><th>Upcoming Inc Chart</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((r, i) => (
                        <tr key={r.entityId || i}>
                          <td><input type="checkbox" checked={!!previewChecked[r.entityId]} onChange={() => togglePreviewOne(r.entityId)} /></td>
                          {fApplyType === "1" && <><td>{r.ccNumber}</td><td>{r.ccName}</td></>}
                          {fApplyType === "2" && <><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerNumber}</td><td>{r.centerName}</td></>}
                          {fApplyType === "3" && <><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.vendorNumber}</td><td>{r.vendorName}</td></>}
                          <td><span className={`badge bg-${r.milkType == 1 ? "info" : r.milkType == 2 ? "warning" : "success"}`}>{milkLbl(r.milkType)}</span></td>
                          <td>{r.incSetting || "\u2014"}</td>
                          <td>{r.existingChart || <span style={{ color: "#6c757d" }}>None</span>}</td>
                          <td style={{ fontWeight: 600, color: "#0d6efd" }}>{r.upcomingChart}</td>
                          <td><span className={`badge bg-${r.status === "Active" ? "success" : "secondary"}`}>{r.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (

        /* Tabs 2-5: Data tables */
        <div className="dashBody" style={{ marginTop: 10 }}>
          <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <h4 style={{ margin: 0 }}>{tabNames[tab - 1]}</h4>
              {canDelete(MID) && ib("#dc3545", "fa-trash", `Delete ${selectedCount}`, () => { if (selectedCount > 0) { setConfirmType("delete"); setconfirm_alert(true) } else alert("Select items first") }, !selectedCount)}
              {isApprovalTab && ib("#28a745", "fa-check", `Approve ${selectedCount}`, () => { if (selectedCount > 0) { setConfirmType("approve"); setconfirm_alert(true) } else alert("Select items first") }, !selectedCount)}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input type="text" className="form-control form-control-sm" placeholder="Search" style={{ width: 180, borderRadius: "4px 0 0 4px" }} value={search} onChange={e => setSearch(e.target.value)} />
              <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: 31 }}><i className="fas fa-search" /></button>
            </div>
          </div>
          <div className="table-responsive">
            <Table className="table mb-0 dairyTbl">
              <thead><tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                {cols.map((c, i) => <th key={i}>{c}</th>)}
              </tr></thead>
              <tbody>
                {tabLoading ? <tr><td colSpan={cols.length + 1}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
                  : paged.length > 0 ? paged.map((r, i) => (
                    <tr key={r.id}>
                      <td><input type="checkbox" checked={!!checkedItems[r.id]} onChange={() => handleCheckbox(r.id)} /></td>
                      <td>{(page - 1) * perPage + i + 1}</td>
                      <td>{getName(companies, r.company)}</td>
                      <td>{getName(allPlants, r.plant)}</td>
                      <td>{getName(allCCs, r.chillingCenter)}</td>
                      {isCenterVendor && <td>{getName(allRoutes, r.route, "routeName")}</td>}
                      {isCenterVendor && <td>{r.vendor ? getName(vendors, r.vendor, "vendorName") : r.collectionCenter ? getName([], r.collectionCenter, "centerName") : "\u2014"}</td>}
                      <td>{getName(charts, r.incentiveChart, "chartName")}</td>
                      <td><span className={`badge bg-${r.milkType == 1 ? "info" : r.milkType == 2 ? "warning" : "success"}`}>{milkLbl(r.milkType)}</span></td>
                      <td>{sessLbl(r.session)}</td>
                      <td style={{ fontSize: 12 }}>{r.effectiveFrom || "\u2014"}</td>
                      <td style={{ fontSize: 12 }}>{r.limitValidity === "1" ? r.validUpto || "\u2014" : "No Limit"}</td>
                      <td>{r.updatedInApp ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                      <td style={{ fontSize: 12 }}>{r.appliedOn ? r.appliedOn.slice(0, 10) : r.approvedAt ? (typeof r.approvedAt === "string" ? r.approvedAt.slice(0, 10) : "\u2014") : r.created_at?.slice(0, 10) || "\u2014"}</td>
                      {isApprovalTab && <td>
                        <button title="Approve" onClick={() => handleSingleApprove(r.id)} style={{ width: 26, height: 26, borderRadius: 4, background: "#28a745", color: "#fff", border: "none", fontSize: 11, cursor: "pointer" }}><i className="fas fa-check" /></button>
                      </td>}
                    </tr>
                  )) : <tr><td colSpan={cols.length + 1} className="text-center py-4">No matching records found</td></tr>}
              </tbody>
            </Table>
          </div>
          {totalPages > 1 && <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 12 }}>
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(1)}>{"\u00AB"}</button>
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{"\u2039"}</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { const s = Math.max(1, Math.min(page - 2, totalPages - 4)); const p = s + i; return p <= totalPages ? <button key={p} className={`btn btn-sm ${p === page ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setPage(p)}>{p}</button> : null })}
            <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{"\u203A"}</button>
            <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>{"\u00BB"}</button>
          </div>}
        </div>
      )}
    </>}

    {confirm_alert && <SweetAlert title={confirmType === "delete" ? `Delete ${selectedCount} item(s)?` : `Approve ${selectedCount} item(s)?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
      onConfirm={() => { handleBulkAction(confirmType); setconfirm_alert(false) }}
      onCancel={() => setconfirm_alert(false)}>{confirmType === "delete" ? "This cannot be undone!" : "Approve selected incentives?"}</SweetAlert>}
    {success_dlg && <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>}
  </>)
}
export default CCIncentives

